import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { FileText, Receipt, Wallet } from "lucide-react";
import { useTranslation } from "react-i18next";

import BillingTrendChart from "./BillingTrendChart";
import FinanceBreakdownCard, {
  buildIssuanceBreakdownRows,
} from "./FinanceBreakdownCard";
import FinanceKpiCard from "./FinanceKpiCard";
import DataTable from "../../common/DataTable/DataTable";
import ExportDateRangeModal from "../../common/ExportDateRangeModal/ExportDateRangeModal";
import PageErrorState from "../../common/PageErrorState/PageErrorState";
import SmallCFLoader from "../../common/SmallCFLoader/SmallCFLoader";
import Toast from "../../common/Toast/Toast";
import EntityConstants from "../../configs/EntityConstants";
import documentExportIcon from "../../resources/icons/document-export-blue.svg";
import { GET } from "../../service/CRUDService";
import { buildQueryString } from "../../utils/buildQueryString";
import {
  classifyApiError,
  getApiErrorCopy,
} from "../../utils/classifyApiError";
import {
  downloadBlob,
  filenameFromContentDisposition,
} from "../../utils/downloadBlob";
import { formatCount, formatPaisaToRupees } from "../../utils/formatCurrency";
import { formatDate } from "../../utils/formatDate";
import { parseSuccessEnvelope } from "../../utils/parseSuccessEnvelope";

const PAGE_LIMIT = 10;
const SEARCH_DEBOUNCE_DELAY = 500;

const ISSUANCE_STATUS_CONFIG = {
  Issued: {
    dotColor: "var(--issued-credential-template-card-status-done-bg)",
    bgColor: "var(--cf-active-bg-color)",
    textColor: "var(--cf-active-text-color)",
    label: "finance:status_issued",
  },
  Notified: {
    dotColor: "var(--cf-info-text-color)",
    bgColor: "var(--cf-info-bg-color)",
    textColor: "var(--cf-info-text-color)",
    label: "finance:status_notified",
  },
  Printed: {
    dotColor: "var(--cf-warning-border-color)",
    bgColor: "var(--email-warning-container-bg)",
    textColor: "var(--cf-warning-text-color)",
    label: "finance:status_printed",
  },
  Printed_and_Notified: {
    dotColor: "var(--issued-screen-tab-button-selected-text-color)",
    bgColor: "var(--issued-screen-tab-button-selected-color)",
    textColor: "var(--issued-screen-tab-button-selected-text-color)",
    label: "finance:status_printed_and_notified",
  },
  Revoked: {
    dotColor: "var(--cf-error-text-color)",
    bgColor: "var(--cf-error-bg-color)",
    textColor: "var(--cf-error-text-color)",
    label: "finance:status_revoked",
  },
};

const IssuanceBillingTab = ({ dateParams }) => {
  const { t } = useTranslation();
  const tRef = useRef(t);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState({
    points: [],
    granularity: null,
    comparison: null,
  });
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [reportRows, setReportRows] = useState([]);
  const [reportCount, setReportCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState({
    summary: true,
    trend: true,
    report: true,
  });
  const [summaryError, setSummaryError] = useState(null);
  const [trendError, setTrendError] = useState(null);
  const [reportError, setReportError] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportRangeModalOpen, setIsExportRangeModalOpen] = useState(false);

  const isMountedRef = useRef(true);
  const summaryRequestId = useRef(0);
  const trendRequestId = useRef(0);
  const reportRequestId = useRef(0);
  const exportRequestId = useRef(0);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    exportRequestId.current += 1;
    setIsExporting(false);
    setIsExportRangeModalOpen(false);
  }, [dateParams]);

  const isAllTimeRange = !dateParams?.start_date && !dateParams?.end_date;

  useEffect(() => {
    const handler = setTimeout(
      () => setSearch(searchInput.trim()),
      SEARCH_DEBOUNCE_DELAY,
    );
    return () => clearTimeout(handler);
  }, [searchInput]);

  const isSearchPending = searchInput.trim() !== search;

  const fetchSummary = useCallback(async () => {
    const requestId = ++summaryRequestId.current;
    setLoading((prev) => ({ ...prev, summary: true }));
    setSummaryError(null);
    try {
      const response = await GET(
        `${EntityConstants.FINANCE_BILLING_SUMMARY}${buildQueryString(dateParams)}`,
      );
      const data = await parseSuccessEnvelope(response);
      if (!isMountedRef.current || requestId !== summaryRequestId.current) {
        return;
      }
      setSummary(data);
    } catch (error) {
      if (!isMountedRef.current || requestId !== summaryRequestId.current) {
        return;
      }
      setSummary(null);
      setSummaryError(classifyApiError(error));
    } finally {
      if (isMountedRef.current && requestId === summaryRequestId.current) {
        setLoading((prev) => ({ ...prev, summary: false }));
      }
    }
  }, [dateParams]);

  const fetchTrend = useCallback(async () => {
    const requestId = ++trendRequestId.current;
    setLoading((prev) => ({ ...prev, trend: true }));
    setTrendError(null);
    try {
      const response = await GET(
        `${EntityConstants.FINANCE_BILLING_TREND}${buildQueryString(dateParams)}`,
      );
      const data = await parseSuccessEnvelope(response);
      if (!isMountedRef.current || requestId !== trendRequestId.current) {
        return;
      }
      setTrend({
        points: data?.points || [],
        granularity: data?.granularity ?? null,
        comparison: data?.comparison ?? null,
      });
    } catch (error) {
      if (!isMountedRef.current || requestId !== trendRequestId.current) {
        return;
      }
      setTrend({ points: [], granularity: null, comparison: null });
      setTrendError(classifyApiError(error));
    } finally {
      if (isMountedRef.current && requestId === trendRequestId.current) {
        setLoading((prev) => ({ ...prev, trend: false }));
      }
    }
  }, [dateParams]);

  const fetchReport = useCallback(
    async (currentOffset) => {
      const requestId = ++reportRequestId.current;
      setLoading((prev) => ({ ...prev, report: true }));
      setReportError(null);
      try {
        const response = await GET(
          `${EntityConstants.FINANCE_BILLING_REPORT}${buildQueryString({
            ...dateParams,
            search,
            limit: PAGE_LIMIT,
            offset: currentOffset,
          })}`,
        );
        const data = await parseSuccessEnvelope(response);
        if (!isMountedRef.current || requestId !== reportRequestId.current) {
          return;
        }
        setReportRows(data?.results || []);
        setReportCount(Number(data?.count) || 0);
        setOffset(currentOffset);
      } catch (error) {
        if (!isMountedRef.current || requestId !== reportRequestId.current) {
          return;
        }
        setReportError(classifyApiError(error));
      } finally {
        if (isMountedRef.current && requestId === reportRequestId.current) {
          setLoading((prev) => ({ ...prev, report: false }));
        }
      }
    },
    [dateParams, search],
  );

  const retryAll = useCallback(() => {
    fetchSummary();
    fetchTrend();
    fetchReport(0);
  }, [fetchSummary, fetchTrend, fetchReport]);

  useEffect(() => {
    fetchSummary();
    fetchTrend();
  }, [fetchSummary, fetchTrend]);

  useEffect(() => {
    fetchReport(0);
  }, [fetchReport]);

  const hasPrevious = offset > 0;
  const hasNext = offset + reportRows.length < reportCount;

  const handlePrevious = () => {
    if (!hasPrevious) return;
    fetchReport(Math.max(0, offset - PAGE_LIMIT));
  };

  const handleNext = () => {
    if (!hasNext) return;
    fetchReport(offset + PAGE_LIMIT);
  };

  const retryTrend = useCallback(() => {
    fetchTrend();
  }, [fetchTrend]);

  const retryReport = useCallback(() => {
    fetchReport(offset);
  }, [fetchReport, offset]);

  const runExport = useCallback(
    async (exportDateParams) => {
      if (isExporting) return;

      const requestId = ++exportRequestId.current;
      setIsExporting(true);
      try {
        const response = await GET(
          `${EntityConstants.FINANCE_BILLING_REPORT_EXPORT}${buildQueryString({
            ...exportDateParams,
            format: "xlsx",
          })}`,
        );

        if (!isMountedRef.current || requestId !== exportRequestId.current) {
          return;
        }

        if (!response) {
          throw new Error(tRef.current("finance:export_failed"));
        }

        if (!response.ok) {
          let message = tRef.current("finance:export_failed");
          try {
            const payload = await response.json();
            message = payload?.message || payload?.error || message;
          } catch {
            // non-JSON error body
          }
          throw new Error(message);
        }

        const blob = await response.blob();
        if (!isMountedRef.current || requestId !== exportRequestId.current) {
          return;
        }

        const filename = filenameFromContentDisposition(
          response.headers.get("Content-Disposition"),
          "issuance-billing.xlsx",
        );
        downloadBlob(blob, filename);

        Toast({
          type: "success",
          title: tRef.current("finance:export_success"),
          message: tRef.current("finance:download_excel"),
        });

        if (isMountedRef.current) {
          setIsExportRangeModalOpen(false);
        }
      } catch (error) {
        if (!isMountedRef.current || requestId !== exportRequestId.current) {
          return;
        }
        Toast({
          type: "error",
          title: tRef.current("finance:export_failed"),
          message: error?.message || tRef.current("finance:export_failed"),
        });
      } finally {
        if (isMountedRef.current && requestId === exportRequestId.current) {
          setIsExporting(false);
        }
      }
    },
    [isExporting],
  );

  const handleExport = useCallback(() => {
    if (isExporting || isSearchPending) return;

    if (isAllTimeRange) {
      setIsExportRangeModalOpen(true);
      return;
    }

    runExport({ ...dateParams, search });
  }, [
    dateParams,
    isAllTimeRange,
    isExporting,
    isSearchPending,
    runExport,
    search,
  ]);

  const breakdown = useMemo(
    () => buildIssuanceBreakdownRows(summary, t),
    [summary, t],
  );

  const columns = useMemo(
    () => [
      {
        key: "issued_at",
        label: t("finance:issued_at"),
        render: (value) => formatDate(value, t("workspace:not_applicable")),
      },
      {
        key: "credential_id",
        label: t("finance:credential_id"),
      },
      {
        key: "credential_name",
        label: t("finance:credential_name"),
        render: (value) =>
          value ? (
            <span className="block max-w-[240px] truncate" title={value}>
              {value}
            </span>
          ) : (
            t("workspace:not_applicable")
          ),
      },
      {
        key: "recipient_name",
        label: t("finance:recipient_name"),
      },
      {
        key: "issued_by",
        label: t("finance:issued_by"),
      },
      {
        key: "organisation_name",
        label: t("finance:organisation"),
      },
      {
        key: "status",
        label: t("finance:status"),
      },
    ],
    [t],
  );

  const exportDisabled =
    isExporting ||
    isSearchPending ||
    reportCount === 0 ||
    loading.report ||
    !!reportError;

  if (!loading.summary && summaryError) {
    const errorCopy = getApiErrorCopy(summaryError, t);
    return (
      <PageErrorState
        variant="section"
        kind={summaryError.kind}
        title={errorCopy.title}
        description={
          errorCopy.description || t("finance:section_load_error_description")
        }
        primaryLabel={t("finance:try_again")}
        onPrimary={retryAll}
        isRetrying={loading.summary}
        details={
          summaryError.status
            ? t("finance:error_details_status", {
                status: summaryError.status,
              })
            : null
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        <FinanceKpiCard
          icon={FileText}
          iconBgClass="bg-cfTemplateTransitionColor2 text-cfPrimary"
          value={formatCount(summary?.credentials_issued)}
          label={t("finance:credentials_issued")}
          subtitle={t("finance:this_billing_cycle")}
          loading={loading.summary}
        />
        <FinanceKpiCard
          icon={Wallet}
          iconBgClass="bg-cfWarningBg text-cfWarningBorder"
          value={formatPaisaToRupees(summary?.subtotal)}
          label={t("finance:current_charges")}
          subtitle={t("finance:excluding_gst")}
          loading={loading.summary}
        />
        <FinanceKpiCard
          icon={Receipt}
          iconBgClass="bg-cfActiveBg text-cfIssuedCredentialTemplateCardStatusDoneBG"
          value={formatPaisaToRupees(summary?.total_amount)}
          label={t("finance:total_bill")}
          subtitle={t("finance:including_gst")}
          loading={loading.summary}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2">
          {trendError && !loading.trend ? (
            <PageErrorState
              variant="section"
              kind={trendError.kind}
              title={getApiErrorCopy(trendError, t).title}
              description={
                getApiErrorCopy(trendError, t).description ||
                t("finance:section_load_error_description")
              }
              primaryLabel={t("finance:try_again")}
              onPrimary={retryTrend}
              isRetrying={loading.trend}
              details={
                trendError.status
                  ? t("finance:error_details_status", {
                      status: trendError.status,
                    })
                  : null
              }
            />
          ) : (
            <BillingTrendChart
              points={trend.points}
              granularity={trend.granularity}
              comparison={trend.comparison}
              loading={loading.trend}
            />
          )}
        </div>
        <FinanceBreakdownCard
          title={t("finance:billing_breakdown_title")}
          subtitle={t("finance:billing_breakdown_subtitle")}
          rows={breakdown.rows}
          totalValue={breakdown.totalValue}
          loading={loading.summary}
        />
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl shadow-xl p-6">
        <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">
              {t("finance:issuance_history")}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {reportRows.length > 0
                ? t("finance:showing_issuance_range", {
                    from: offset + 1,
                    to: offset + reportRows.length,
                    total: reportCount,
                  })
                : t("finance:showing_issuance_records", {
                    shown: reportRows.length,
                    total: reportCount,
                  })}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap w-full md:w-auto">
            <form
              onSubmit={(e) => e.preventDefault()}
              className="bg-cfSecondary p-2 rounded-lg w-full md:w-[25rem] border border-gray-400"
            >
              <input
                type="text"
                name="search"
                id="billing-issuance-input-search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t("finance:search_placeholder")}
                aria-label={t("finance:search_placeholder")}
                className="outline-none border-none p-0 placeholder:text-sm w-full"
                style={{ fontSize: "0.875rem", lineHeight: "1.25rem" }}
              />
            </form>
            <button
              type="button"
              disabled={exportDisabled}
              onClick={handleExport}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-cfPrimaryButton hover:bg-cfPrimaryDark rounded-[8px] disabled:bg-cfDisabled disabled:cursor-not-allowed transition-colors"
            >
              {isExporting ? (
                <SmallCFLoader text={t("finance:exporting")} />
              ) : (
                <>
                  <img
                    src={documentExportIcon}
                    alt=""
                    className="w-4 h-4 brightness-0 invert"
                  />
                  {t("finance:download_excel")}
                </>
              )}
            </button>
          </div>
        </div>
        {reportError && !loading.report ? (
          <PageErrorState
            variant="section"
            kind={reportError.kind}
            title={getApiErrorCopy(reportError, t).title}
            description={
              getApiErrorCopy(reportError, t).description ||
              t("finance:section_load_error_description")
            }
            primaryLabel={t("finance:try_again")}
            onPrimary={retryReport}
            isRetrying={loading.report}
            details={
              reportError.status
                ? t("finance:error_details_status", {
                    status: reportError.status,
                  })
                : null
            }
          />
        ) : (
          <DataTable
            columns={columns}
            data={reportRows}
            loading={loading.report}
            loadingVariant="skeleton"
            skeletonRowCount={PAGE_LIMIT}
            emptyMessage={
              search
                ? t("finance:no_issuance_records_search")
                : t("finance:no_issuance_records")
            }
            emptyHint={
              search ? t("finance:no_issuance_records_search_hint") : undefined
            }
            rowKey="credential_id"
            statusConfig={ISSUANCE_STATUS_CONFIG}
            align="center"
            hasPrevious={hasPrevious}
            hasNext={hasNext}
            onPrevious={handlePrevious}
            onNext={handleNext}
          />
        )}
      </div>

      <ExportDateRangeModal
        isOpen={isExportRangeModalOpen}
        onClose={() => setIsExportRangeModalOpen(false)}
        onConfirm={(exportDateParams) =>
          runExport({ ...exportDateParams, search })
        }
        isExporting={isExporting}
        testId="issuance-billing-export-range-modal"
      />
    </div>
  );
};

export default IssuanceBillingTab;
